<template>
  <DatePicker
    ref="datepicker"
    v-bind="$attrs"
    class="app-date-picker"
    :class="rootClasses"
    :value="value"
    :value-type="valueType"
    :type="type"
    :range="range"
    :format="format"
    :placeholder="placeholder"
    :disabled="disabled"
    :clearable="clearable"
    :disabled-date="istDatumDeaktiviert"
    :get-classes="getClasses"
    :lang="langConfig"
    :append-to-body="appendToBody"
    :popup-class="popupClasses"
    v-on="$listeners"
    @open="merkeAngezeigtenMonat"
    @calendar-change="merkeAngezeigtenMonat"
    @panel-change="merkeAngezeigtenMonat"
    @close="angezeigterMonat = null"
  >
    <template #icon-calendar>
      <Calendar :size="16" class="app-date-picker__icon" />
    </template>

    <template #icon-clear>
      <X :size="16" class="app-date-picker__icon" />
    </template>

    <template v-if="hatHeader" #header="slotProps">
      <slot name="header" v-bind="slotProps" />
    </template>

    <template v-if="hatSidebar" #sidebar="slotProps">
      <slot name="sidebar" v-bind="slotProps" />
    </template>

    <!-- der Footer entsteht nur, wenn die Aktionszeile möglich ist oder ein Slot befüllt wurde -->
    <template v-if="hatFooter" #footer="slotProps">
      <div class="app-date-picker__footer">
        <div v-if="zeigeAktionen" class="app-date-picker__aktionen">
          <button
            v-if="heuteButton"
            type="button"
            class="btn dialog-btn-secondary app-date-picker__aktion"
            :disabled="heuteNichtWaehlbar"
            @click="heuteWaehlen(slotProps.emit)"
          >
            <Calendar :size="14" />
            Heute
          </button>
          <!-- der Rücksprung erscheint erst, sobald ein anderer Monat angezeigt wird -->
          <button
            v-if="zeigeMonatZurueck"
            type="button"
            class="btn dialog-btn-secondary app-date-picker__aktion app-date-picker__aktion--rechts"
            @click="zuAktuellemMonat"
          >
            <Undo2 :size="14" />
            Aktueller Monat
          </button>
        </div>
        <slot name="footer" v-bind="slotProps" />
      </div>
    </template>
  </DatePicker>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import { startOfDay, startOfMonth } from 'date-fns';
import { Calendar, Undo2, X } from 'lucide-vue';
import { langConfig } from '@/core/common/helpers/utils.js';

function zuTagesbeginn(wert) {
  if (!wert) {
    return null;
  }
  const datum = wert instanceof Date ? new Date(wert) : new Date(wert);
  if (Number.isNaN(datum.getTime())) {
    return null;
  }
  datum.setHours(0, 0, 0, 0);
  return datum;
}

export default {
  name: 'AppDatePickerComponent',
  components: {
    DatePicker,
    Calendar,
    Undo2,
    X,
  },
  inheritAttrs: false,
  props: {
    value: { type: [Date, String, Number, Array], default: null },
    valueType: { type: String, default: 'date' },
    type: { type: String, default: 'date' },
    range: { type: Boolean, default: false },
    format: { type: String, default: 'DD.MM.YYYY' },
    placeholder: { type: String, default: '' },
    min: { type: [Date, String], default: null },
    max: { type: [Date, String], default: null },
    disabledDate: { type: Function, default: null },
    // vue2-datepicker ruft die Funktion für jede Zelle auf, deshalb darf sie nie null sein
    getClasses: { type: Function, default: () => [] },
    // Validierungszustand wie bei b-form-*: true färbt den Rahmen gültig, false ungültig, null bleibt neutral
    state: { type: Boolean, default: null },
    size: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    heuteButton: { type: Boolean, default: true },
    // das Popup hängt an body, sobald es sonst vom Container abgeschnitten würde, etwa im AG Grid
    appendToBody: { type: Boolean, default: false },
    popupClass: { type: String, default: '' },
  },
  data() {
    return {
      langConfig,
      angezeigterMonat: null,
    };
  },
  computed: {
    // das Popup wird global über app-date-picker-popup.css gestylt, weil es bei
    // appendToBody außerhalb der Komponente liegt
    popupClasses() {
      return ['app-date-picker-popup', { 'app-date-picker-popup--sm': this.size === 'sm' }, this.popupClass];
    },
    rootClasses() {
      return {
        'app-date-picker--sm': this.size === 'sm',
        'app-date-picker--invalid': this.state === false,
        'app-date-picker--valid': this.state === true,
      };
    },
    // die Footer-Aktionen greifen nur auf dem Tagespanel, der Range-Kalender hat eine andere API
    zeigeAktionen() {
      return !this.range && this.type === 'date';
    },
    hatFooter() {
      return this.zeigeAktionen || Boolean(this.$scopedSlots.footer || this.$slots.footer);
    },
    // heute lässt sich nicht immer wählen, etwa an Wochenenden oder außerhalb von min und max
    heuteNichtWaehlbar() {
      return this.istDatumDeaktiviert(startOfDay(new Date()), null);
    },
    zeigeMonatZurueck() {
      if (!this.zeigeAktionen || !this.angezeigterMonat) {
        return false;
      }
      const heute = new Date();
      return (
        this.angezeigterMonat.getFullYear() !== heute.getFullYear() ||
        this.angezeigterMonat.getMonth() !== heute.getMonth()
      );
    },
    hatHeader() {
      return Boolean(this.$scopedSlots.header || this.$slots.header);
    },
    hatSidebar() {
      return Boolean(this.$scopedSlots.sidebar || this.$slots.sidebar);
    },
    minGrenze() {
      return zuTagesbeginn(this.min);
    },
    maxGrenze() {
      return zuTagesbeginn(this.max);
    },
  },
  methods: {
    // min und max werden mit dem übergebenen Predicate verknüpft, damit die Aufrufer das nicht selbst bauen
    istDatumDeaktiviert(date, currentValue) {
      if (this.minGrenze && date < this.minGrenze) {
        return true;
      }
      if (this.maxGrenze && date > this.maxGrenze) {
        return true;
      }
      return this.disabledDate ? this.disabledDate(date, currentValue) : false;
    },
    // von außen aufrufbar, damit eigene Footer-Aktionen den Kalender nach der Auswahl schließen können
    closePopup() {
      if (this.$refs.datepicker) {
        this.$refs.datepicker.closePopup();
      }
    },
    // greift auf die interne Kalender-Instanz des Plugins zu, die keine öffentliche API für den Monat anbietet
    kalenderPanel() {
      return (this.$refs.datepicker && this.$refs.datepicker.$refs.picker) || null;
    },
    // der angezeigte Monat wird nach jedem Navigationsschritt aus dem Panel gelesen
    merkeAngezeigtenMonat() {
      this.$nextTick(() => {
        const panel = this.kalenderPanel();
        this.angezeigterMonat = panel ? new Date(panel.innerCalendar) : null;
      });
    },
    // die Auswahl läuft über den emit des Footer-Slots, damit value-type und das Schließen passen
    heuteWaehlen(emit) {
      if (this.heuteNichtWaehlbar) {
        return;
      }
      emit(startOfDay(new Date()), this.type);
    },
    zuAktuellemMonat() {
      const panel = this.kalenderPanel();
      if (!panel) {
        return;
      }
      // aus der Jahres- oder Monatsauswahl wird zuerst in die Tagesansicht zurückgewechselt
      if (panel.panel !== 'date') {
        panel.panel = 'date';
      }
      panel.handleCalendarChange(startOfMonth(new Date()), 'month');
    },
  },
};
</script>

<style scoped src="./app-date-picker.css"></style>
<style src="./app-date-picker-popup.css"></style>
